/**
 * Realised (historical) volatility: the one formula (README "HV30 is one
 * formula").
 *
 * Pure and dependency-free, so the scanner's bake, the configurable
 * indicator, the option chain and the Recommend screen all import it.
 *
 *   realizedVol(closes, n) = sqrt( (1/n) * sum (r_i - mean r)^2 ) * sqrt(252)
 *
 * over the n daily log returns r_i = ln(c_i / c_{i-1}) of the LAST n + 1
 * closes of `closes` (oldest first): the POPULATION standard deviation
 * (divided by n, not n - 1), as TradingView's `ta.stdev` and the scanner's
 * `vol_sd_30` / `hv_20` columns always were, annualised by 252 sessions. A
 * fraction: 0.32 is 32%.
 *
 * Null when it cannot be measured: fewer than n + 1 closes, or any of the
 * last n + 1 not a positive finite price. A shorter window is never read as
 * the full one, a bad close is never stepped over (that would make a
 * two-day return of it), and nothing stands in for a missing figure.
 *
 * Until 2026-10-15 the app had three copies of HV30 that disagreed for one
 * name: the chain's (`hv30Of`: sample deviation, from as few as ten
 * returns), Recommend's (`hv30FromCloses`: sample deviation over the closes
 * of 37 calendar days, about 24 returns) and the scanner's (population, the
 * full 30). The Options bar's HV30 and Trade Management's risk view (which
 * reads the scanner's column) could then show two numbers for one stock.
 * The scanner's was kept: it is the stored column (and an ML feature and a
 * backtest criterion), and its convention is TradingView's.
 */

/** Sessions a year: the annualisation of a daily deviation. */
export const SESSIONS_PER_YEAR = 252;

/** The returns HV30 is read over. */
export const HV30_RETURNS = 30;

export function realizedVol(closes: ReadonlyArray<number>, returns: number): number | null {
  if (!(returns >= 1) || closes.length < returns + 1) return null;
  const from = closes.length - (returns + 1);
  const rets: number[] = [];
  for (let i = from + 1; i < closes.length; i++) {
    const a = closes[i - 1]!;
    const b = closes[i]!;
    if (!(a > 0) || !(b > 0) || !Number.isFinite(a) || !Number.isFinite(b)) return null;
    rets.push(Math.log(b / a));
  }
  const mean = rets.reduce((s, x) => s + x, 0) / rets.length;
  const variance = rets.reduce((s, x) => s + (x - mean) ** 2, 0) / rets.length;
  return Math.sqrt(variance) * Math.sqrt(SESSIONS_PER_YEAR);
}

/** HV30: the realised volatility of the last 30 daily returns (31 closes). */
export function hv30(closes: ReadonlyArray<number>): number | null {
  return realizedVol(closes, HV30_RETURNS);
}
